import { Box, BoxProps, CircularProgress, SvgIcon, Tooltip, Typography, alpha } from '@mui/material';
import { ChevronDown as ExpandMoreIcon } from '@opengov/react-capital-assets';
import React from 'react';

import { OpenGovIconMenuItem } from '../Components/icons/OpenGovIconMenuItem';
import { CAPITAL_DESIGN_TOKENS, NAV_COLOR_PALETTE, tooltipOffsetProps } from '../Components/styleConstants';
import { DesktopContextSwitcher } from '../ContextSwitcher/EntitySwitcher/DesktopContextSwitcher';

import { DATA_TEST } from '../enums';
import { IContextSwitcherData } from '../types';
import { shouldEnableSubmenu } from './utils';

interface IApplicationNameWithContextSwitcherProps {
  /** The name of the current application */
  appName: string;
  /** Callback to close any open submenus */
  closeOpenSubmenu: () => void;
  /** Context switcher data containing entities and applications */
  contextSwitcherData?: IContextSwitcherData;
  forceContextSwitcherVisibility?: boolean;
}
export const ApplicationNameWithContextSwitcher: React.FC<IApplicationNameWithContextSwitcherProps> = ({
  closeOpenSubmenu,
  appName,
  contextSwitcherData
}) => {
  const [showContextSwitcherSubmenu, setShowContextSwitcherSubmenu] = React.useState(false);
  const firstContextSwitcherItemRef = React.useRef<HTMLLIElement>(null);
  const applicationNameRef = React.useRef<HTMLDivElement>(null);

  const enableSubmenu = shouldEnableSubmenu(contextSwitcherData);

  const showLoadingSpinner = contextSwitcherData?.loading;
  const hasLoadingError = !!contextSwitcherData?.loadingError;
  const isNotLoading = !showLoadingSpinner;

  // Show dropdown caret when submenu is enabled and not loading, or when there's an error and not loading
  const showDropdownCaret = (enableSubmenu && isNotLoading) || (hasLoadingError && isNotLoading);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!enableSubmenu) return;
    if (!showContextSwitcherSubmenu) closeOpenSubmenu();
    if (e.key === 'ArrowDown' && firstContextSwitcherItemRef?.current) {
      firstContextSwitcherItemRef.current?.focus();
    }
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!enableSubmenu) return;
    if (!showContextSwitcherSubmenu) closeOpenSubmenu();
  };

  const conditionalBoxProps = (isInteractive: boolean): BoxProps => {
    if (isInteractive) {
      return {
        'aria-label': 'Switch application',
        'aria-haspopup': 'menu',
        component: 'button',
        tabIndex: 0,
        paddingBlockEnd: 0
      };
    }
    return {
      component: 'div',
      'aria-disabled': true
    };
  };

  // Component is interactive when it shows a dropdown caret or loading spinner
  const isInteractive = showDropdownCaret || showLoadingSpinner;
  return (
    <>
      <Box display="flex" alignItems="center" height="100%" role="menu" data-test={DATA_TEST.nav_app_name_container}>
        <OpenGovIconMenuItem />
        <Tooltip
          data-test={enableSubmenu && DATA_TEST.context_switcher_tooltip}
          title={enableSubmenu && 'Switch application'}
          placement="top"
          enterNextDelay={500}
          disableInteractive={true}
          {...tooltipOffsetProps}
        >
          <Box
            {...conditionalBoxProps(Boolean(isInteractive))}
            role="menuitem"
            ref={applicationNameRef}
            data-test={DATA_TEST.context_switcher_trigger}
            display="flex"
            alignItems="center"
            height={32}
            onKeyDown={handleKeyDown}
            onClick={(e) => {
              handleClick(e);
              setShowContextSwitcherSubmenu((s) => !s);
            }}
            sx={{
              height: '100%',
              pr: isInteractive ? 0 : 1,
              pl: 0.5,
              pt: 0.5,
              display: 'flex',
              alignItems: 'center',
              background: 'transparent',
              border: 'none',
              ':hover': {
                cursor: enableSubmenu ? 'pointer' : 'inherit',
                backgroundColor: enableSubmenu
                  ? (theme) => theme.palette.mode === 'dark'
                    ? alpha(theme.palette.primary.main, 0.12)
                    : theme.palette.primary.light
                  : 'inherit'
              },
              '&.Mui-focusVisible, &:focus-visible': {
                outline: (theme: any) => `2px solid ${theme.palette.primary.main}`,
                outlineOffset: -2,
                borderRadius: 1
              }
            }}
          >
            <Typography
              sx={{
                lineHeight: 1,
                pb: 0.5,
                height: '100%',
                alignContent: 'center',
                whiteSpace: 'nowrap',
                fontFamily: CAPITAL_DESIGN_TOKENS.foundations.typography.baseFont,
                fontWeight: CAPITAL_DESIGN_TOKENS.foundations.typography.fontWeight.semiBold
              }}
              variant="h3"
              component="span"
              color={NAV_COLOR_PALETTE.primaryBlue}
            >
              {appName}
            </Typography>
            {showLoadingSpinner && (
              <CircularProgress
                sx={{ mx: '4px', color: NAV_COLOR_PALETTE.primaryBlue }}
                size={12}
                thickness={8}
                data-test={DATA_TEST.context_switcher_loading}
                aria-label="Loading context switcher"
              />
            )}
            {showDropdownCaret && (
              <SvgIcon
                inheritViewBox
                component={ExpandMoreIcon}
                data-test={DATA_TEST.context_switcher_dropdown_arrow}
                sx={{ color: NAV_COLOR_PALETTE.primaryBlue }}
              />
            )}
          </Box>
        </Tooltip>
      </Box>
      <DesktopContextSwitcher
        loadingError={contextSwitcherData?.loadingError}
        contextSwitcherData={contextSwitcherData as IContextSwitcherData}
        anchorEl={applicationNameRef.current as HTMLDivElement}
        showContextSwitcher={(enableSubmenu || hasLoadingError) && showContextSwitcherSubmenu}
        closeSubmenu={() => setShowContextSwitcherSubmenu(false)}
        firstContextSwitcherItemRef={firstContextSwitcherItemRef as React.MutableRefObject<HTMLLIElement>}
      />
    </>
  );
};
